/**
 * @documental
 * @export Placeholder
 * @name
 * TextArea.Placeholder
 */
import { FC } from "react";
import Skeleton from "react-loading-skeleton";

import { createComponentStyleHook } from "../../tokens";

import { AccessibilityProps } from "../../AccessibilityProps";
import { BaseProps } from "../../BaseProps";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { InputContainerPlaceholder } from "../shared/InputContainer/InputContainerPlaceholder";

import tokenStyles from "./styles.module.css";
import legacyStyles from "./styles.module.scss";

/**
 * The `Placeholder` component properties.
 */

export interface PlaceholderProps extends BaseProps, AccessibilityProps {
  /**
   * The number of lines displayed in the text area.
   */
  readonly lines?: number;

  /**
   * Indicates whether the text area has instructions. If not specified defaults to `false`.
   */
  readonly hasInstructions?: boolean;
}

const useResolvedTextAreaPlaceholderStyles = createComponentStyleHook<PlaceholderProps>({
  legacyStyles,
  tokenStyles,
});

/**
 * A loading placeholder for the `TextArea` component.
 */
export const Placeholder: FC<PlaceholderProps> = (props) => {
  const { lines, hasInstructions = false, "aria-label": ariaLabel } = props;
  const { mode, styles, StyleModeBoundary } = useResolvedTextAreaPlaceholderStyles(props);

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [
      styles.TextAreaPlaceholder,
      "text-area",
      "placeholder",
      {
        "fixed-height": lines !== undefined,
      },
    ],
    testIdPrefix: "text-area-placeholder",
  });

  return (
    <StyleModeBoundary mode={mode}>
      <InputContainerPlaceholder {...getBasePropsFromHtmlProps(baseHtmlProps)} hasInstructions={hasInstructions}>
        <div className={styles.PlaceholderContent}>
          <textarea className="input" rows={lines} readOnly disabled aria-label={ariaLabel} />
          <Skeleton />
        </div>
      </InputContainerPlaceholder>
    </StyleModeBoundary>
  );
};
